import { Injectable } from '@angular/core';
import { ItemSelection } from '@shared/components/customization/customization.component';

@Injectable({
    providedIn: 'root'
})
export class CustomizationService {
    constructor() {}

    convertCustomizationStringToCustomizationModel(customizationString = null, menuData: any) {
        if (!customizationString) {
            return {};
        }

        const customizationIds = customizationString.split('-');
        const model = {};

        customizationIds.forEach(id => {
            const typeId = menuData.product_customizations[id].product_customization_type_id;
            model[typeId] = [...(model[typeId] || []), Number(id)];
        });

        return model;
    }

    getCustomizationArray(customizationModel: { [n: number]: number[] }): number[] {
        return Object.values(customizationModel).reduce((a, b) => [...a, ...b], []);
    }

    getCustomizationString(customizationModel: { [n: number]: number[] }): string {
        return this.getCustomizationArray(customizationModel)
            .sort((a, b) => a - b)
            .join('-');
    }

    getDefaultCustomizationsString(customizationTypes) {
        const customizationModel = this.getDefaultCustomizations(customizationTypes);
        return this.getCustomizationString(customizationModel);
    }

    getDefaultCustomizations(customizationTypes) {
        return customizationTypes
            .map(customizationType => {
                // all the !sold_out customizations of intrinsic customizationType should be added
                // add all the dflt and !sold_out customization for a customizationType
                // customizationType should have min number of customizations added
                let customizations = [];
                if (customizationType.intrinsic) {
                    customizations = customizationType.customizations
                        .map(customization => !customization.sold_out && customization.id)
                        .filter(Boolean);
                } else {
                    const notSelectedCustomizations = [];
                    const notSelectedCustomizationsIterator = notSelectedCustomizations[Symbol.iterator]();

                    // if selected then return id
                    // else add to local variable and return false through: `![].push() => false`
                    customizations = customizationType.customizations
                        .map(customization =>
                            !customization.sold_out
                                ? customization.dflt
                                    ? customization.id
                                    : !notSelectedCustomizations.push(customization.id)
                                : false
                        )
                        .filter(Boolean);
                    customizations.length = customizationType.min;

                    customizations = Array.from(customizations).map(
                        customizationId => customizationId || notSelectedCustomizationsIterator.next().value
                    );
                }
                return { [customizationType.id]: customizations.filter(Boolean).sort((a, b) => a - b) };
            })
            .reduce((a, b) => ({ ...a, ...b }), {});
    }

    getAllProductSize(productId, menuData) {
        return menuData.products[productId].product_sizes;
    }

    getAllCustomizationTypes(sizeId, menuData) {
        const { product_customization_types } = menuData.product_sizes[sizeId];
        return product_customization_types ? product_customization_types : [];
    }

    toShowCustomizationModal(productId, menuData) {
        // return true if we have more than 1 productSize OR 1 or more non-intrinsic customizationsType and customizationsType has atleast one non soldout customization
        const productSizes = this.getAllProductSize(productId, menuData);
        const customizationTypes = this.getAllCustomizationTypes(productSizes[0].id, menuData).filter(customizationType => {
            return !customizationType.intrinsic && !customizationType.sold_out;
        });

        return productSizes.length > 1 || customizationTypes.length >= 1;
    }

    getCustomizationsObject(customizationIdArray, menuData: any) {
        return customizationIdArray.map(id => menuData.product_customizations[id]).filter(Boolean);
    }

    isSelectionValid(selection: ItemSelection, menuData: any): { valid: boolean; message?: string } {
        let invalidProductCustomizationType = null;
        const isValid = Object.entries(selection.customizations).every(([customizationTypeId, customizationsIds]) => {
            const temp = menuData.product_customization_types[customizationTypeId];
            const { min, max } = menuData.product_customization_types[customizationTypeId];
            if (min <= customizationsIds.length && customizationsIds.length <= max) {
                return true;
            } else {
                invalidProductCustomizationType = temp;
                return false;
            }
        });

        return {
            valid: isValid,
            ...(isValid ? {} : { message: `Please select ${invalidProductCustomizationType.label}` })
        };
    }
}
